/* eslint-disable react-hooks/exhaustive-deps */
import { Box, Typography, Dialog, DialogTitle, DialogContent, IconButton } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import DataTable from "../../../common/DataTable";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { showNotification } from "../../../common/Notification/ToastifyNotification";
import { apiRequest } from "../../../common/common";
import { LoadingPage } from "../../../common/LoadingPage";
import { ActivityColDefs } from "./columnDefs";
import moment from "moment/moment";
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { useDispatch, useSelector } from "react-redux";
import ColumnPanel from "../../../common/ColumnPanel";
import HistoryFilterPanel from "./HistoryFilterPanel";
import { getUsers } from "../../../redux/Actions/userActions";

const HistoryModule = ({
    projectId,
}) => {
    const dispatch = useDispatch();
    const { user } = useSelector((state) => state.users);
    const [rowsPerPage, setRowPerPage] = useState(10);
    const [dataLoading, setDataLoading] = useState(false);
    const [gridApi, setGridApi] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedChanges, setSelectedChanges] = useState([]);
    const [activityColumn, setActivityColumn] = useState(ActivityColDefs);
    const [settingData, setSettingData] = useState([]);

    const onPaginationChanged = (event) => {
        if (event.api) {
            const newPageSize = event.api.paginationGetPageSize();
            setRowPerPage(newPageSize);
        }
    };

    const getDataSource = (filters) => {
        if (filters) {
            localStorage.setItem('auditfilters', JSON.stringify(filters));
        }
        const filter = localStorage.getItem('auditfilters') ? JSON.parse(localStorage.getItem('auditfilters')) : filters;
        let filterParams = {};
        if (filter?.tableName?.length > 0) filterParams.table = filter.tableName;
        if (filter?.startdate) filterParams.from = filter.startdate;
        if (filter?.enddate) filterParams.to = filter.enddate;
        if (filter?.objectName) filterParams.search = filter.objectName;
        if (filter?.action) filterParams.action = filter.action;
        if (filter?.userId?.length > 0) filterParams.userId = filter.userId;
        if (filter?.recordId?.length > 0) filterParams.recordId = filter.recordId;
        return {
            rowCount: null,
            getRows: async (params) => {
                if (projectId) {
                    try {
                        const queryParams = new URLSearchParams({
                            page: Math.floor((params.startRow / rowsPerPage) + 1),
                            limit: rowsPerPage,
                            projectId: projectId,
                            ...filterParams
                        });

                        const response = await apiRequest('get', `/audit-log?${queryParams.toString()}`);
                        const { data, pagination } = response.data;
                        let lastRow = -1;
                        if (data?.length <= pagination.total) {
                            lastRow = pagination.total;
                        }
                        params.successCallback(data, lastRow);
                        setDataLoading(false);
                    } catch (error) {
                        setDataLoading(false);
                        showNotification(error?.response?.data.error || error.message, 'error');
                        params.failCallback();
                    }
                }
            },
        };
    };

    const onGridReady = useCallback((params) => {
        if (params?.api) {
            const { api } = params;
            setGridApi(api);
            setDataLoading(true);
            api.setGridOption("datasource", getDataSource());
            setTimeout(() => {
                const node = api.getRenderedNodes();
                if (!node?.[0]?.displayed) {
                    api.showNoRowsOverlay();
                }
            }, 5000);
        }
    }, [rowsPerPage, projectId]);

    useEffect(() => {
        let isMounted = true;
        if (isMounted && gridApi) {
            gridApi.setGridOption("datasource", getDataSource());
        }
        return () => {
            isMounted = false;
        };
    }, [rowsPerPage, projectId]);

    // Helper function to get all changed fields
    const getChangedFields = (changes) => {
        if (!changes) return [];
        return Object.keys(changes).filter(key => changes[key] && (changes[key].old !== undefined || changes[key].new !== undefined));
    };

    // Transform changes data for the popup grid
    const transformChangesForGrid = (changes) => {
        if (!changes) return [];

        const formatValue = (value) => {
            if (value === null || value === undefined) return '----';
            if (typeof value === 'boolean') return value ? 'Yes' : 'No';
            if (typeof value === 'object') return JSON.stringify(value);
            return String(value);
        };

        const formatFieldName = (field) => {
            return field
                .replace(/_id$/i, '')        // remove trailing _id
                .replace(/_/g, ' ')          // replace all underscores with space
                .trim();
        };

        // Format 2: { new: { field1: value1, ... } } — new record added
        if (changes.new && typeof changes.new === 'object' && !changes.old) {
            return Object.entries(changes.new)
                .filter(([field, value]) => value !== null && value !== undefined)
                .map(([field, value]) => ({
                    field_name: formatFieldName(field),
                    prev_value: '----',
                    new_value: formatValue(value)
                }));
        }

        // Format 1: { field: { old: ..., new: ... } } — field-level changes
        const changedFields = getChangedFields(changes);
        return changedFields.map(field => ({
            field_name: formatFieldName(field),
            prev_value: formatValue(changes[field]?.old),
            new_value: formatValue(changes[field]?.new),
        }));
    };

    const handleViewChanges = (changes) => {
        const changesArray = transformChangesForGrid(changes);
        setSelectedChanges(changesArray);
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setSelectedChanges([]);
    };

    // Column definitions for the changes popup grid
    const changesColumnDefs = [
        {
            field: 'field_name',
            headerName: 'Field',
            width: 200,
            sortable: true,
            filter: true
        },
        {
            field: 'prev_value',
            headerName: 'Previous Value',
            width: 300,
            sortable: true,
            filter: true,
            wrapText: true,
            autoHeight: true,
            cellStyle: (params) => {
                if (params.value && params.value !== '--' && params.value !== '----') {
                    return { color: '#d32f2f', fontWeight: '500' }; // red for old value
                }
                return {};
            }
        },
        {
            field: 'new_value',
            headerName: 'New Value',
            width: 300,
            sortable: true,
            filter: true,
            wrapText: true,
            autoHeight: true,
            cellStyle: (params) => {
                if (params.value && params.value !== '--' && params.value !== '----') {
                    return { color: '#2e7d32', fontWeight: '500' }; // green for new value
                }
                return {};
            }
        }
    ];


    useEffect(() => {
        if (activityColumn?.length > 0 && gridApi) {
            const columnState = gridApi?.getColumnState()?.map(state => {
                const columnConfig = activityColumn.find((c) => c.colId === state.colId || c.field === state.colId);
                if (columnConfig) {
                    if (columnConfig.pinned !== state.pinned) {
                        state.pinned = columnConfig.pinned ? columnConfig.pinned : null;
                    }
                }

                return state;
            });
            gridApi?.applyColumnState({ state: columnState });
            gridApi?.refreshHeader();
        }
    }, [activityColumn]);


    const getData = async () => {
        try {
            setDataLoading(true)
            const response = await apiRequest('get', `/user-view?userId=${user?.user_id}&type=audit&pageNo=1&pageSize=50`);
            const { data } = response.data;
            if (data?.length > 0) {
                if (data?.find((a) => a.is_pinned === true)?.columns) {
                    setActivityColumn([...activityColumn.slice(0, 1), ...data?.find((a) => a.is_pinned === true)?.columns]);
                }
                setSettingData(data);
            } else {
                setSettingData([]);
            }
            setDataLoading(false);
        } catch (error) {
            setDataLoading(false);
            showNotification(error?.response?.data.error || error.message, 'error');
        }
    }

    useEffect(() => {
        if (user?.user_id) {
            getData();
        }
    }, [user?.user_id]);

    const orgId = user?.organization?.organization_id;
    const calledRef = useRef(null);

    useEffect(() => {
        if (orgId && calledRef.current !== orgId) {
            calledRef.current = orgId;
            dispatch(getUsers(orgId));
        }
    }, [orgId]);

    const toolPanel = useMemo(() => [
        {
            id: 'custom1',
            labelDefault: 'Columns',
            iconKey: "columns",
            toolPanel: ColumnPanel,
            width: 450,
            toolPanelParams: {
                columns: activityColumn?.slice(1, 8),
                setColumns: (column) => {
                    setActivityColumn([...activityColumn.slice(0, 1), ...column]);
                },
                settingData,
                fetchData: () => {
                    if (user?.user_id) {
                        getData();
                    }
                },
                type: "audit",
                defaultVisibleColumns: ActivityColDefs?.slice(1, 8),
                columnOrder: activityColumn?.slice(1, 8),
            }
        },
        {
            id: 'custom2',
            iconKey: "filter",
            labelDefault: 'Filters',
            toolPanel: HistoryFilterPanel,
            width: 300,
            toolPanelParams: {
                projectId,
                applyFilter: (filters) => {
                    if (gridApi) {
                        setRowPerPage(10);
                        gridApi.setGridOption("datasource", getDataSource(filters));
                    }
                }
            }
        }
    ], [gridApi, settingData, activityColumn]);


    const updatedColumns = useMemo(() => {
        const column = activityColumn;
        if (column.length > 0) {
            return column?.map((col) => {
                switch (col.field) {
                    case '':
                        return {
                            ...col,
                            cellRenderer: (params) => params?.data && (
                                <Box
                                    sx={{ cursor: 'pointer' }}
                                    onClick={() => handleViewChanges(params?.data?.changes)}
                                >
                                    <VisibilityOutlinedIcon color="primary" />
                                </Box>
                            )
                        }
                    case 'user':
                        return {
                            ...col,
                            cellRenderer: (params) => params?.data && (
                                <>
                                    {`${params?.data?.changed_user?.first_name || ''} ${params?.data?.changed_user?.last_name || ''}`.trim() || '--'}
                                </>
                            ),
                        }
                    case 'object_name':
                        return {
                            ...col,
                            cellRenderer: (params) => params?.data?.object_name || '--',
                        }
                    case 'table_name':
                        return {
                            ...col,
                            cellRenderer: (params) => params?.data?.table_name || '--',
                        }
                    case 'created_at':
                        return {
                            ...col,
                            cellRenderer: (params) => params?.data && (
                                <>
                                    {moment(params?.data?.created_at).format('DD-MM-YYYY HH:mm A')}
                                </>
                            ),
                        }
                    default:
                        return col;
                }
            });
        }
        return column;
    }, [activityColumn]);

    return (
        <Box mb={1} mt={1}>
            {dataLoading && (<LoadingPage isLoading={dataLoading} />)}
            <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }} mb={1}>
                <Typography variant="body1" style={{ fontWeight: 'bold' }}>History</Typography>
            </Box>
            <DataTable
                height="60vh"
                columnDefs={updatedColumns}
                rowsPerPage={rowsPerPage}
                paginationPageSizeSelector={[10, 15, 25, 50, 100]}
                onPaginationChanged={onPaginationChanged}
                singleClickEdit={false}
                modelType="infinite"
                selectionType="single"
                onGridReady={onGridReady}
                fromTemplate={true}
                toolPanel={toolPanel}
            />

            {/* Dialog Popup for Changes */}
            <Dialog
                open={dialogOpen}
                onClose={handleCloseDialog}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6">Field Changes Details</Typography>
                        <IconButton onClick={handleCloseDialog} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent dividers>
                    {selectedChanges.length > 0 ? (
                        <Box sx={{ height: '400px', width: '100%' }}>
                            <DataTable
                                height="100%"
                                columnDefs={changesColumnDefs}
                                rowData={selectedChanges}
                                paginationPageSize={10}
                                paginationPageSizeSelector={[5, 10, 25]}
                                modelType="clientSide"
                                fromTemplate={false}
                            />
                        </Box>
                    ) : (
                        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                            <Typography variant="body1" color="textSecondary">
                                No changes to display
                            </Typography>
                        </Box>
                    )}
                </DialogContent>
            </Dialog>
        </Box>
    )
}

export default HistoryModule;